import { isC3Experience } from "../util/C3Utils";
define((require) => {
    "use strict";

    const LoggedIn          = require("shared/security/LoggedIn");
    const Configuration     = require("shared/services/Configuration");
    const Class             = require("shared/util/Class");

    /**
     * Singleton managing access to the REST API.
     *
     * @class RestApi
     * @constructor
     */
    const RestApi = Class.extend({

        /**
         * The collections available in the database.
         * @constant
         */
        Collections: {
            ACTIONS: "actions",
            ANALYTICS: "analytics",
            CHANNEL_CONFIG_CHANNELS: "channelConfigs/channels",
            CHANNELS: "channels",
            CHANNELS_PUSH: "channels/push",
            CHANNELS_UNPUSHED: "channels/unpushed",
            CHANNEL_CONFIG: "channelConfigs",
            CHANNELS_COUNT: "channels/count",
            CHANNEL_CONFIGS_COUNT: "channelConfigs/channels/count",
            COMBINED: "combined",
            COMMENTS: "comments",
            COMPANIES: "companies",
            CONTENTS: "contents",
            CONTENTS_COUNT: "contents/count",
            EXPERIENCES: "experiences",
            EXPERIENCES_META: "experiences/meta",
            EXPERIENCE_CODES: "experience_codes",
            FONTS: "fonts",
            GROUPS: "groups",
            GROUPS_ASSETS: "groups/assets",
            IMAGES: "images",
            IMAGES_COUNT: "images/count",
            INTEGRATIONS: "integrations",
            PROJECTS: "projects",
            PROJECTS_META: "projects/meta",
            PROJECTS_ALL_CONTENTS: "projects/contents",
            PUBLISHABLES: "contents/publishables",
            PUBLISHABLES_COUNT: "contents/publishables/count",
            SCENES: "scenes",
            SETTINGS: "settings",
            SHARES: "shares",
            SCENE7_IMAGES: "images/scene7",
            TEMPLATES: "templates",
            TEMPLATES_COUNT: "templates/count",
            TEMPLATES_PUBLISHABLE: "templates/publishable",
            TRAFFIC: "traffic",
            USERS: "users",
            WIDGETS: "widgets",
            EXPERIENCE_EMAIL_SEND: "messaging/api/email/company/send"
        },

        // PE-1216: These are collections supported by new C3 API which will determine which API_URL will be used
        C3Collections: [
            'images',
            'experiences'
        ],

        /**
         * The URL to the API.
         */
        API_URL: null,

        /**
         * Let us access the settings for the company
         */
        COMPANY_SETTINGS: null,

        /**
         * The path to the proxy server in this environment.
         */
        PROXY_URL: null,

        /**
         * Initialize the REST API client. Must be called prior to using the API.
         *
         * @param {String} baseUrl The base URL to the API.
         * @param {String } proxyUrl the URL to get to proxy for the given environment.
         */
        init(baseUrl, proxyUrl = null) {
            this.API_URL = `${baseUrl}/api`;
            this.PROXY_URL = proxyUrl;
            // PE-1216: LEGACY_API_URL will be used to toggle the value of API_URL between the new C3 API URL and the LEGACY URL
            // depending on the collection required
            this.LEGACY_API_URL = `${baseUrl}/api`;
        },

        /**
         * Qualifies the specified path with the base url of the REST api.
         *
         * @param {String} [path] The path to be qualified; can be falsey, in which case the base url is simply returned,
         *                        with no trailing slash. If already prefixed with the REST api base url, <tt>path</tt>
         *                        is simply returned as is.
         * @return {String} The qualified path; will be falsey if the base url has not been initialized.
         */
        qualify(path) {
            let url = this.API_URL;
            if(path === "time") {
                this.API_URL = `${Configuration.adminApiBaseUrl}/api`
                url = this.API_URL + "/time";
                return url;
            }
            if (url && path) {
                // Do not prefix if already prefix...
                if (path.indexOf(url) !== 0) {
                    // Make sure we get exactly a single slash between the base url and the path...
                    if (path.charAt(0) !== "/") {
                        path = `/${path}`;
                    }
                    url += path;
                }
            }
            return url;
        },

        /**
         * Generates a path to a specific resource.
         *
         * @param {String} collection The collection containing the resource.
         * @param {String} id The id of the resource.
         * @returns {String} The path to the resource.
         */
        pathToSpecificResource(collection, id) {
            let url;
            const newDashboardExperience = isC3Experience();
            if (newDashboardExperience && this.C3Collections.includes(collection)) {
                // directly using Configuration.c3ApibaseUrl in url is not working (i.e Auth Token missing from request header)
                this.API_URL = `${Configuration.c3ApibaseUrl}/v1`;
                url = `${this.API_URL}/${collection}/${id}`
            }
            else {
                this.API_URL = this.LEGACY_API_URL;
                url = `${this.API_URL}/${collection}/${id}`;
            }
            return url;
        },

        /**
         * Generates a path to a collection.
         *
         * @param {String} collection The collection.
         * @param {boolean} useAlternate if true, the code will use an alternate URL (to conditionally replace functionality.)
         * @returns {String} The path to the collection.
         */
        pathToCollection(collection, useAlternate = false) {
            if (useAlternate) {
                const companyId =  LoggedIn.principal().get("companyId");
                // Temporary hack until we get per-company configuration working.
                // This array is a mix of staging and production company ids we want to try the alternate dashboard
                // call with.  If the company id isn't in this list, it goes back to using the regular call.
                const companyIndex = ["5bbe70ef6b7ecf180f9600a2", // Derek staging
                    "5b215af9464bf670c5a43f7fX", // esmith staging
                    "5b215b60464bf670c5a43fa8"  // Dom staging
                ].indexOf(companyId);

                let altUrl = null;

                if (companyIndex !== -1) {
                    if (this.Collections.CONTENTS === collection) {
                        altUrl = `${this.PROXY_URL}/experiences/api/contents`;
                    }

                    if (altUrl) {
                        return `${altUrl}`;
                    }
                }
            }
            // If useAlternate is false, if the company id isn't in the array, or if the alternate url is missing...
            // then we use the original/default URL.
            const newDashboardExperience = isC3Experience();
            if (newDashboardExperience && this.C3Collections.includes(collection)) {
                this.API_URL = `${Configuration.c3ApibaseUrl}/v1`;
            } else {
                // PE-1216: Due to changing of API_URL in the pathToSpecificResource, we need to make sure that
                // it reverts back to the legacy base uri for legacy endpoints
                this.API_URL = this.LEGACY_API_URL;
            }
            return `${this.API_URL}/${collection}`;
        },

        /**
         * Generates a path to a collection belonging to a specific resource.
         *
         * @param {String} collection The collection containing the resource.
         * @param {String} id The id of the resource.
         * @param {String} subCollection The collection belonging to the resource.
         * @param {Boolean} [useProxy=false] Defines whenever to use proxy url or API url by default
         * @returns {String} The path to the collection belonging to the specific resource.
         */
        pathToCollectionOnResource(collection, id, subCollection, useProxy = false) {
            return `${useProxy ? `${this.PROXY_URL}/experiences/api` : this.API_URL}/${collection}/${id}/${subCollection}`;
        },

        /**
         * Generates a path to a specific resource.
         *
         * @param {String} resourceCollection The collection containing the resource.
         * @param {String} resourceId The id of the resource.
         * @param {String} subResourceCollection The collection containing the subresource.
         * @param {String} subResourceId The id of the subresource.
         * @param {String} collection The collection.
         * @returns {String} The path to the resource.
         */
        pathToCollectionOnSubResource(resourceCollection, resourceId, subResourceCollection, subResourceId, collection) {
            return `${this.API_URL}/${resourceCollection}/${resourceId}/${subResourceCollection}/${subResourceId}/${collection}`;
        }

    });

    return new RestApi();
});



// WEBPACK FOOTER //
// ./src/shared/services/RestApi.js